<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useFetchOperations } from "~/core/composables/useFetchOperations";
import { SearchMode } from "~/core/composables/useSearch";
import { DEFAULT_FIRST_PAGE } from "~/core/constants/constants";
import { Filters } from "~/core/models/filters";
import { formatIsoDate } from "~/core/utils/date";
import type { CampaignOperationMgr } from "~/modules/campaign/models/CampaignOperationMgr.interface";
import { useFetchDtwinModels } from "~/modules/dtwin/composables/useFetchDtwinModels";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import { PATH_DTWINS_ID } from "~/modules/dtwin/constants";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";
import { getConnectivityStatusParameters } from "~/modules/dtwin/utils/dtwinsListConnectivityStatus";
import { getDtwinTypeLabel } from "~/modules/dtwin/utils/getDtwinTypeLabel";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";

const props = defineProps<{
  campaign?: CampaignOperationMgr;
}>();

const { t } = useI18n();
const router = useRouter();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const { selectedSectorCodes } = storeToRefs(useSectorStore());

// Activate or deactivate the use of page and pageSize parameters in URL
// Limitation when activated: the fetch of dtwins is done twice
const useQueryParametersForPagination = true;

const { error, fetchData, results, setFilter, onSearch } = useFetchOperations({
  searchMode: SearchMode.FILTER_SEARCH,
  useQueryParametersForPagination,
  organizationCode: currentOrganization.value?.code,
});

const {
  error: errorDtwins,
  setFilter: setDtwinFilter,
  fetchData: fetchDtwins,
  results: dtwins,
} = useFetchDtwins({
  searchMode: SearchMode.FILTER_SEARCH,
  organizationCode: currentOrganization.value?.code,
});

const { getSectorName: getSectorLabel } = useSectorStore();

const {
  error: errorAllDtwinModels,
  results: resultsAllDtwinModels,
  fetchAll: fetchAllDtwinModels,
} = useFetchDtwinModels({ organizationCode: currentOrganization.value?.code });

const isLoading: Ref<boolean> = ref(false);

async function loadData(page?: number, pageSize?: number) {
  if (props.campaign) {
    isLoading.value = true;

    setFilter(Filters.OPERATION_CAMPAIGN_UID, props.campaign.uid);
    setFilter(Filters.SECTORS, selectedSectorCodes.value || []);

    const { pageSize: pageSizeParam } = await fetchData(page, pageSize);

    const dtwinsUid = results.value?.results.map(
      (operation) => operation.deviceTwinUid,
    );

    if (dtwinsUid) {
      setDtwinFilter(Filters.DTWIN_UID, dtwinsUid);
    }

    await Promise.all([
      fetchAllDtwinModels(),
      fetchDtwins(DEFAULT_FIRST_PAGE, pageSizeParam),
    ]);

    isLoading.value = false;
  }
}

const getDtwin = (uid: string) => {
  return dtwins.value?.results.find((dtwin) => dtwin.uid === uid);
};

const getSectorName = (dtwinUid: string) => {
  const sectorUid = dtwins.value?.results.find(
    (dtwin) => dtwin.uid === dtwinUid,
  )?.sectorCode;
  if (sectorUid) {
    return getSectorLabel(sectorUid);
  } else {
    return "";
  }
};

function onRowClick(dtwinOperation: DtwinOperation<Record<string, string>>) {
  router.push({
    name: PATH_DTWINS_ID,
    params: { id: dtwinOperation.deviceTwinUid },
  });
}

onSearch(loadData);

watch(selectedSectorCodes, () => {
  loadData();
});
</script>
<template>
  <h4 class="mt-0 mb-4 leading-7 py-2">
    {{ t("campaign.details.equipments.title") }}
  </h4>
  <lxc-table
    min-width="82rem"
    :is-loading="isLoading"
    :error="
      error?.toError() ||
      errorAllDtwinModels?.toError() ||
      errorDtwins?.toError()
    "
    :data="results?.results"
    :context="results?.pagination"
    :empty-text="t('campaign.details.equipments.empty')"
    clickable
    @row-click="onRowClick"
    @change-page-and-page-size="loadData"
  >
    <lxc-table-column
      :label="t('campaign.details.equipments.column.name.label')"
      min-width="10rem"
    >
      <template #default="scope">
        {{ getDtwin(scope.row.deviceTwinUid)?.attributes.friendlyName }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      :label="t('campaign.details.equipments.column.deviceType.label')"
      width="8rem"
    >
      <template v-if="resultsAllDtwinModels" #default="scope">
        {{
          getDtwinTypeLabel(
            resultsAllDtwinModels,
            getDtwin(scope.row.deviceTwinUid),
          )
        }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      :label="t('campaign.details.equipments.column.serialNumber.label')"
      width="12rem"
    >
      <template #default="scope">
        {{ getDtwin(scope.row.deviceTwinUid)?.attributes.serialNumber }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      :label="t('campaign.details.equipments.column.firmware.label')"
      width="8rem"
    >
      <template #default="scope">
        {{
          getDtwin(scope.row.deviceTwinUid)?.features?.firmware?.reported
            ?.firmwareVersion
        }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      :label="t('campaign.details.equipments.column.connectivity.label')"
      class="!py-0"
      width="8rem"
    >
      <template #default="scope">
        <dtwin-connectivity-status
          v-if="getDtwin(scope.row.deviceTwinUid)?.connectivityStatus"
          :connectivity-status="
            getDtwin(scope.row.deviceTwinUid)?.connectivityStatus
          "
          :connectivity-status-parameters="
            getConnectivityStatusParameters(
              getDtwin(scope.row.deviceTwinUid)?.deviceModelUid,
              resultsAllDtwinModels,
            )
          "
          :center="true"
        />
      </template>
    </lxc-table-column>
    <lxc-table-column
      :label="t('campaign.details.equipments.column.metastatus.label')"
      class="!py-0"
      width="8rem"
    >
      <template #default="scope">
        <dtwin-metadata-status
          v-if="getDtwin(scope.row.deviceTwinUid)?.operationStatuses"
          :operation-statuses="
            getDtwin(scope.row.deviceTwinUid)?.operationStatuses
          "
        />
      </template>
    </lxc-table-column>
    <lxc-table-column
      :label="t('campaign.details.equipments.column.sector.label')"
      width="10rem"
    >
      <template #default="scope">
        {{ getSectorName(scope.row.deviceTwinUid) }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      :label="t('campaign.details.equipments.column.lastUpdate.label')"
      width="10rem"
    >
      <template #default="scope">
        {{
          formatIsoDate(
            scope.row.updatedAt,
            t("campaign.list.column.dates.format"),
          )
        }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      :label="t('campaign.details.equipments.column.operationStatus.label')"
      class="!py-0"
      width="8rem"
    >
      <template #default="scope">
        <operation-mgr-operation-status-badge :status="scope.row.status" />
      </template>
    </lxc-table-column>
  </lxc-table>
</template>
